// Color extraction utility
export const extractColorFromClass = (className: string): string => {
  // Return hex colors as-is
  if (className.startsWith('#')) return className;

  // Handle gradient classes
  if (className.includes('gradient')) {
    const matches = className.match(/from-(\w+)-(\d+)/);
    if (matches) {
      return getColorValue(matches[1], matches[2]);
    }
  }

  // Handle regular color classes (bg-*, text-*, border-*)
  const colorMatch = className.match(/(?:bg|text|border)-(\w+)-(\d+)/);
  if (colorMatch) {
    return getColorValue(colorMatch[1], colorMatch[2]);
  }

  // Handle special colors without numbers
  if (className.includes('text-white') || className.includes('bg-white')) return '#ffffff';
  if (className.includes('text-black') || className.includes('bg-black')) return '#000000';
  if (className.includes('transparent')) return 'transparent';

  // Handle text-gray-100, text-gray-900 etc without prefix
  const simpleColorMatch = className.match(/(\w+)-(\d+)/);
  if (simpleColorMatch) {
    return getColorValue(simpleColorMatch[1], simpleColorMatch[2]);
  }

  return '#6b7280'; // Default gray
};

// Map Tailwind colors to hex values
export const getColorValue = (color: string, shade: string): string => {
  const colors: Record<string, Record<string, string>> = {
    gray: {
      '50': '#f9fafb',
      '100': '#f3f4f6',
      '200': '#e5e7eb',
      '300': '#d1d5db',
      '400': '#9ca3af',
      '500': '#6b7280',
      '600': '#4b5563',
      '700': '#374151',
      '800': '#1f2937',
      '900': '#111827',
    },
    red: {
      '50': '#fef2f2',
      '100': '#fee2e2',
      '200': '#fecaca',
      '300': '#fca5a5',
      '400': '#f87171',
      '500': '#ef4444',
      '600': '#dc2626',
      '700': '#b91c1c',
      '800': '#991b1b',
      '900': '#7f1d1d',
    },
    blue: {
      '50': '#eff6ff',
      '100': '#dbeafe',
      '200': '#bfdbfe',
      '300': '#93c5fd',
      '400': '#60a5fa',
      '500': '#3b82f6',
      '600': '#2563eb',
      '700': '#1d4ed8',
      '800': '#1e40af',
      '900': '#1e3a8a',
    },
    purple: {
      '50': '#faf5ff',
      '100': '#f3e8ff',
      '200': '#e9d5ff',
      '300': '#d8b4fe',
      '400': '#c084fc',
      '500': '#a855f7',
      '600': '#9333ea',
      '700': '#7e22ce',
      '800': '#6b21a8',
      '900': '#581c87',
    },
    pink: {
      '50': '#fdf2f8',
      '100': '#fce7f3',
      '200': '#fbcfe8',
      '300': '#f9a8d4',
      '400': '#f472b6',
      '500': '#ec4899',
      '600': '#db2777',
      '700': '#be185d',
      '800': '#9d174d',
      '900': '#831843',
    },
    orange: {
      '50': '#fff7ed',
      '100': '#ffedd5',
      '200': '#fed7aa',
      '300': '#fdba74',
      '400': '#fb923c',
      '500': '#f97316',
      '600': '#ea580c',
      '700': '#c2410c',
      '800': '#9a3412',
      '900': '#7c2d12',
    },
    yellow: {
      '50': '#fefce8',
      '100': '#fef3c7',
      '200': '#fde68a',
      '300': '#fcd34d',
      '400': '#fbbf24',
      '500': '#f59e0b',
      '600': '#d97706',
      '700': '#b45309',
      '800': '#92400e',
      '900': '#78350f',
    },
    primary: {
      '50': '#eff6ff',
      '100': '#dbeafe',
      '200': '#bfdbfe',
      '300': '#93c5fd',
      '400': '#60a5fa',
      '500': '#3b82f6',
      '600': '#2563eb',
      '700': '#1d4ed8',
      '800': '#1e40af',
      '900': '#1e3a8a',
    },
  };

  return colors[color]?.[shade] || '#6b7280';
};

// Define which properties are actually colors for each component
export const colorProperties: Record<string, string[]> = {
  navbar: ['background', 'text', 'brandText', 'linkText', 'linkHover'],
  hero: ['background', 'text'],
  features: ['background', 'cardBg', 'iconColor', 'titleColor', 'textColor'],
  footer: ['background', 'text', 'mutedText'],
  cta: ['background', 'text'],
  form: ['background', 'inputBg', 'inputText', 'labelColor'],
  pricing: ['background', 'cardBg', 'titleColor', 'textColor', 'priceColor'],
  team: ['background', 'titleColor', 'textColor'],
  mediaText: ['background', 'titleColor', 'textColor'],
  textBlocks: ['background', 'titleColor', 'textColor'],
  stats: [
    'background',
    'titleColor',
    'subtitleColor',
    'valueColor',
    'labelColor',
    'descriptionColor',
  ],
};
